import { Briefcase, Clock, MapPin, User, Wrench } from 'lucide-react';
import type React from 'react';
import { formatOffsetTime } from '../../lib/job-plan-time';
import { cn } from '../../lib/utils';
import type { Job } from '../../types/job-plan';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { JOB_STATUS_COLOR_MAP } from './JobCard';

/** Tri-state value for the "select all" header checkbox. */
export function getJobsTableSelectAllState(
  selectedCount: number,
  totalCount: number
): boolean | 'indeterminate' {
  if (selectedCount === 0) return false;
  if (selectedCount === totalCount) return true;
  return 'indeterminate';
}

export interface JobsTableColumnCountOptions {
  showServiceDate: boolean;
  hasRowActions: boolean;
  selectable: boolean;
}

/** Column count driving colSpan for the loading/empty rows -- must track every conditional TableHead below. */
export function getJobsTableColumnCount({
  showServiceDate,
  hasRowActions,
  selectable,
}: JobsTableColumnCountOptions): number {
  return 7 + (showServiceDate ? 1 : 0) + (hasRowActions ? 1 : 0) + (selectable ? 1 : 0);
}

export interface JobsTableProps {
  items: Job[];
  isLoading: boolean;
  selectedId?: number;
  onRowClick: (jobId: number) => void;
  renderRowActions?: (job: Job) => React.ReactNode;
  emptyLabel?: string;
  showServiceDate?: boolean;
  selectedIds?: Set<number>;
  onToggleSelected?: (jobId: number) => void;
  onToggleSelectAll?: () => void;
}

export function JobsTable({
  items,
  isLoading,
  selectedId,
  onRowClick,
  renderRowActions,
  emptyLabel = 'No jobs found',
  showServiceDate = false,
  selectedIds,
  onToggleSelected,
  onToggleSelectAll,
}: JobsTableProps) {
  const selectable = selectedIds !== undefined && onToggleSelected !== undefined;
  const columnCount = getJobsTableColumnCount({
    showServiceDate,
    hasRowActions: !!renderRowActions,
    selectable,
  });

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {selectable && (
            <TableHead className="w-10">
              <Checkbox
                checked={getJobsTableSelectAllState(selectedIds!.size, items.length)}
                onCheckedChange={onToggleSelectAll}
                aria-label="Select all jobs"
              />
            </TableHead>
          )}
          <TableHead className="w-[280px]">Job</TableHead>
          <TableHead>Status</TableHead>
          {showServiceDate && <TableHead>Date</TableHead>}
          <TableHead>Contact</TableHead>
          <TableHead>Window</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead>Skills</TableHead>
          <TableHead>Operator</TableHead>
          {renderRowActions && <TableHead className="text-right">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {isLoading ? (
          Array(5)
            .fill(0)
            .map((_, i) => (
              <TableRow key={i}>
                {Array(columnCount)
                  .fill(0)
                  .map((__, j) => (
                    <TableCell key={j}>
                      <div className="h-4 w-24 bg-muted animate-pulse rounded" />
                    </TableCell>
                  ))}
              </TableRow>
            ))
        ) : items.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columnCount} className="h-64 text-center">
              <div className="flex flex-col items-center justify-center space-y-2">
                <Briefcase className="h-10 w-10 text-muted-foreground opacity-20" />
                <p className="text-sm text-muted-foreground">{emptyLabel}</p>
              </div>
            </TableCell>
          </TableRow>
        ) : (
          items.map((job) => {
            const statusClass = JOB_STATUS_COLOR_MAP[job.status] || JOB_STATUS_COLOR_MAP.unassigned;
            const hasWindow =
              job.windowStartOffsetSeconds != null && job.windowEndOffsetSeconds != null;
            const windowStr = hasWindow
              ? `${formatOffsetTime(job.windowStartOffsetSeconds!)} - ${formatOffsetTime(
                  job.windowEndOffsetSeconds!
                )}`
              : null;
            const durationMin = Math.round(job.estimatedDurationSeconds / 60);

            const isSelected = selectedIds?.has(job.id) ?? false;

            return (
              <TableRow
                key={job.id}
                data-state={isSelected ? 'selected' : undefined}
                className={cn(
                  'group transition-colors cursor-pointer hover:bg-slate-50/50 dark:hover:bg-slate-900/50',
                  selectedId === job.id && 'bg-slate-100/50 dark:bg-slate-900/80',
                  job.status === 'completed' && 'opacity-70'
                )}
                onClick={() => onRowClick(job.id)}
              >
                {selectable && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => onToggleSelected!(job.id)}
                      aria-label={`Select ${job.title}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium max-w-[280px]">
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span className="truncate">{job.title}</span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground font-normal truncate">
                      <MapPin className="h-3 w-3 shrink-0 text-muted-foreground/70" />
                      <span className="truncate">{job.formattedAddress}</span>
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className={cn('text-[11px] capitalize', statusClass)}>
                    {job.status.replace('_', ' ')}
                  </Badge>
                </TableCell>
                {showServiceDate && (
                  <TableCell className="text-xs text-muted-foreground">{job.serviceDate}</TableCell>
                )}
                <TableCell className="max-w-[180px]">
                  {job.contact ? (
                    <div className="flex items-center gap-1.5 min-w-0">
                      <User className="h-3.5 w-3.5 shrink-0 text-primary/70" />
                      <span className="text-xs text-muted-foreground truncate">
                        {job.contact.name}
                        {job.contact.company ? ` (${job.contact.company})` : ''}
                      </span>
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground block">—</span>
                  )}
                </TableCell>
                <TableCell className="max-w-[160px]">
                  {windowStr ? (
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      {windowStr}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{durationMin}m</TableCell>
                <TableCell className="max-w-[180px]">
                  {job.requiredSkills && job.requiredSkills.length > 0 ? (
                    <div className="flex flex-wrap items-center gap-1">
                      <Wrench className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                      {job.requiredSkills.map((skill) => (
                        <span
                          key={skill.id}
                          style={{
                            backgroundColor: skill.color ? `${skill.color}20` : undefined,
                            color: skill.color || undefined,
                            borderColor: skill.color ? `${skill.color}40` : undefined,
                          }}
                          className="px-1.5 py-0.5 text-[10px] font-medium rounded border border-border/40 bg-secondary/50"
                        >
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                <TableCell className="max-w-[160px]">
                  {job.assignedOperator?.operatorName ? (
                    <span className="text-xs text-muted-foreground truncate block">
                      {job.assignedOperator.operatorName}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </TableCell>
                {renderRowActions && (
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {renderRowActions(job)}
                  </TableCell>
                )}
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  );
}
